import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ClientHeader } from '../components/client/ClientHeader';
import { LoadingCard, EmptyState } from '../components/client/clientShared';
import { Users, Phone, MapPin, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientProvidersPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API_URL}/api/client/providers`, { headers });
        setProviders(res.data || []);
      } catch (err) {
        toast.error('Could not load your providers');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ClientHeader title="My Providers" subtitle="Providers you've worked with" backTo="/client/dashboard" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? <LoadingCard /> : providers.length === 0 ? (
          <EmptyState icon={<Users className="h-10 w-10 opacity-40" />} title="No providers yet"
            subtitle="Book a service or consultation and your providers will appear here."
            cta="Find a Provider" onCta={() => navigate('/')} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {providers.map((p) => (
              <Card key={p.id} data-testid={`provider-${p.id}`}>
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold truncate">{p.displayName}</h3>
                    {p.providerCategory && <Badge variant="secondary" className="text-xs shrink-0">{p.providerCategory}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {p.bookingCount} booking{p.bookingCount !== 1 ? 's' : ''}
                  </p>
                  {(p.city || p.state) && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {[p.city, p.state].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {p.phone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {p.phone}
                    </p>
                  )}
                  {p.slug && (
                    <Button size="sm" variant="outline" className="w-full mt-2" onClick={() => navigate(`/providers/${p.slug}`)} data-testid={`view-provider-${p.id}`}>
                      <ExternalLink className="h-3 w-3 mr-1" /> View & Book
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
